import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Table, CellText, Avatar, Spinner, Alert, EmptyState, Plus } from '@opendate-app/setlist';
import crmLabels from '../../shared/utils/crmLabels';

function initialsFor(first, last) {
  return `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';
}

function RecentlyAddedContacts({ contacts, teamId, loading, error, labels = crmLabels(undefined) }) {
  const rows = contacts || [];

  const columns = [
    {
      key: 'contact',
      header: labels.contact.singular,
      primary: true,
      render: (c) => {
        const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ') || '(No name)';
        return (
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <Avatar size="sm" src={c.avatar_thumb_url} initials={initialsFor(c.first_name, c.last_name)} alt={fullName} />
            <CellText
              primary={(
                <Button as="a" href={`/teams/${teamId}/crm/fans/${c.id}`} variant="link-color">
                  {fullName}
                </Button>
              )}
              supporting={c.email || undefined}
            />
          </div>
        );
      },
    },
    {
      key: 'org',
      header: labels.organization.singular,
      render: (c) => c.company_name || '—',
    },
    {
      key: 'added',
      header: 'Added',
      render: (c) => (c.created_at
        ? new Date(c.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : '—'),
    },
  ];

  return (
    <Card padding="md">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <strong>{`Recently added ${labels.contact.plural.toLowerCase()}`}</strong>
        <a href={`/teams/${teamId}/crm/fans`}>{`View all ${labels.contact.plural.toLowerCase()}`}</a>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-4"><Spinner label="Loading…" /></div>
      ) : error ? (
        <Alert color="error" description={typeof error === 'string' ? error : 'Failed to load contacts'} />
      ) : (
        <Table
          columns={columns}
          data={rows}
          getRowKey={(c) => c.id}
          empty={(
            <EmptyState
              size="sm"
              title="No contacts yet"
              description={`Import a CSV or add contacts one at a time — ${labels.organization.plural.toLowerCase()} are created automatically from email domains.`}
              primaryAction={{ label: 'New contact', icon: Plus, onClick: () => { window.location.href = `/teams/${teamId}/crm/fans?action=new`; } }}
            />
          )}
        />
      )}
    </Card>
  );
}

RecentlyAddedContacts.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.object),
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  labels: PropTypes.object,
};

export default RecentlyAddedContacts;
